import { useEffect, useMemo, useState } from 'react';
import { get, isEmpty } from 'lodash';
import { useRestApi } from 'app/common/hooks';
import { useInventoryInfo } from 'app/common/hooks/inventory';
import {
  IN_PROGRESS,
  PENDING
} from 'app/common/hooks/useRestApi/RequestStatus.js';

/**
 * Hook to fetch inventory information for a cart item.
 *
 * @param {string|string[]} sku - The SKU or array of SKUs to fetch inventory for.
 * @returns {{ locationQtys: Object, loading: boolean, error: any }}
 */
const useCartItemInventory = sku => {
  const { availability } = useInventoryInfo();
  const [locationQtys, setLocationQtys] = useState({});

  const skuParam = useMemo(() => {
    if (Array.isArray(sku)) {
      return sku.join(',');
    }
    return sku;
  }, [sku]);

  const {
    response,
    loading,
    error,
    requestStatus,
    sendCallback: fetchInventory
  } = useRestApi(
    availability.baseUrl,
    {
      params: {
        skuFieldReferences: skuParam
      }
    },
    false
  );

  useEffect(() => {
    if (
      requestStatus === IN_PROGRESS ||
      !isEmpty(locationQtys) ||
      !sku ||
      isEmpty(sku)
    ) {
      return;
    }

    if (error) {
      return;
    }

    if (requestStatus === PENDING) {
      fetchInventory();
    }
  }, [requestStatus, locationQtys, sku, error, fetchInventory]);

  useEffect(() => {
    if (response && Array.isArray(response) && response.length > 0) {
      const qtys = response.reduce((acc, item) => {
        const skuCode = get(item, 'skuRef.skuCode');
        const quantity = get(item, 'quantityAvailable', 0);
        const inventoryLocationId = get(item, 'inventoryLocationId');

        if (!acc[skuCode]) {
          acc[skuCode] = {};
        }

        acc[skuCode][inventoryLocationId] = quantity;

        return acc;
      }, {});
      setLocationQtys(qtys);
    } else {
      setLocationQtys({});
    }
  }, [response]);

  return {
    locationQtys,
    loading,
    error,
    requestStatus
  };
};

export default useCartItemInventory;
